import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage as FM, injectIntl } from 'react-intl';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { DefaultButton, RadioButton, ReduxSelectField } from '../../../../common/components';

import { staticStyles } from './style';
import { FormMessageHOC } from '../../../../common';
import { getTradingAccountList } from '../../../_redux';
import { getWalletAccountsList } from '../../../multiWallet/_redux';
import { restartChallenge } from '../../_redux/actions';

const RestartChallengeForm = ({
  challengeId,
  challengeCost,
  isWalletAccountAddonEnabled,
  isMultiWalletAddonEnabled,
  getWalletAccounts,
  walletAccounts,
  accounts,
  accountsIsLoaded,
  baseValue,
  getTradingAccountList,
  handleSubmit,
  account,
  withdrawAccount,
  submitting,
  invalid,
  dispatch,
  error,
  restartButtonTextId,
  userChallengesParams,
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState(
    isWalletAccountAddonEnabled || isMultiWalletAddonEnabled ? 'wallet' : 'tradingAccount'
  );
  const [refundMethod, setRefundMethod] = useState(
    isWalletAccountAddonEnabled || isMultiWalletAddonEnabled ? 'wallet' : 'tradingAccount'
  );

  useEffect(() => {
    if (!accountsIsLoaded) {
      getTradingAccountList();
    }
  }, [getTradingAccountList]);

  useEffect(() => {
    if (isMultiWalletAddonEnabled) {
      getWalletAccounts();
    }
  }, []);

  const submit = handleSubmit(({ walletPaymentAccount, walletRefundAccount }) => {
    restartChallenge(
      {
        id: challengeId,
        account: paymentMethod !== 'wallet' ? account : walletPaymentAccount || undefined,
        withdrawAccount: refundMethod !== 'wallet' ? withdrawAccount : walletRefundAccount || undefined,
        userChallengesParams,
        onSuccess,
      },
      dispatch
    );
  });

  const accountsFiltered = useMemo(
    () => accounts.filter(acc => !acc.isDemo && !acc.isBlocked && !acc.loading),
    [accounts]
  );

  const accountsOptions = accountsFiltered.map(item => {
    const balance = baseValue === 'balance' ? item.balance : item.marginFree;
    return {
      label: `${item.accountTypeTitle} : ${item.login} ${balance ? `( ${balance.amount} ${balance.currency} )` : ''}`,
      value: item.id,
    };
  });

  const walletAccountsOptions = walletAccounts.map(item => ({
    label: `${item.accountTypeTitle} : ${item.login} ${
      item.balance ? `( ${item.balance.amount} ${item.balance.currency} )` : ''
    }`,
    value: item.id,
  }));

  const isSubmitButtonDisabled = useMemo(() => {
    if (!account && paymentMethod !== 'wallet') {
      return true;
    }
    if (!withdrawAccount && refundMethod !== 'wallet') {
      return true;
    }

    return invalid || submitting;
  }, [account, withdrawAccount, invalid, submitting, paymentMethod, refundMethod]);

  return (
    <form className="RestartChallengeForm" onSubmit={submit}>
      <div
        className="RestartChallengeForm__PaymentMethod"
        style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '25px' }}>
        <FM id="justPaymentMethodForChallenge">{txt => <span className="challenge-text">{txt}:</span>}</FM>
        {isWalletAccountAddonEnabled && (
          <RadioButton
            name="paymentMethod"
            textId="justWithWallet"
            value="wallet"
            onChange={() => setPaymentMethod('wallet')}
            checked={paymentMethod === 'wallet'}
          />
        )}

        {isMultiWalletAddonEnabled && (
          <>
            <RadioButton
              name="paymentMethod"
              textId="justWalletTitle"
              value="wallet"
              onChange={() => setPaymentMethod('wallet')}
              checked={paymentMethod === 'wallet'}
            />
            {paymentMethod === 'wallet' && (
              <Field
                component={ReduxSelectField}
                name="walletPaymentAccount"
                textId="justWalletTitle"
                options={walletAccountsOptions}
              />
            )}
          </>
        )}

        {!isWalletAccountAddonEnabled && !isMultiWalletAddonEnabled && (
          <>
            <RadioButton
              name="paymentMethod"
              textId="justWithTradingAccount"
              value="tradingAccount"
              onChange={() => setPaymentMethod('tradingAccount')}
              checked={paymentMethod === 'tradingAccount'}
            />
            {paymentMethod === 'tradingAccount' && (
              <Field component={ReduxSelectField} name="account" textId="justAccount" options={accountsOptions} />
            )}
          </>
        )}
      </div>

      <div
        className="RestartChallengeForm__RefundMethod"
        style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '25px' }}>
        <FM id="justRefundMethodForChallenge">{txt => <span className="challenge-text">{txt}:</span>}</FM>
        {isWalletAccountAddonEnabled && (
          <RadioButton
            name="refundMethod"
            textId="justToMyWallet"
            value="wallet"
            onChange={() => setRefundMethod('wallet')}
            checked={refundMethod === 'wallet'}
          />
        )}

        {isMultiWalletAddonEnabled && (
          <>
            <RadioButton
              name="refundMethod"
              textId="justToMyWallet"
              value="wallet"
              onChange={() => setRefundMethod('wallet')}
              checked={refundMethod === 'wallet'}
            />
            {refundMethod === 'wallet' && (
              <Field
                component={ReduxSelectField}
                name="walletRefundAccount"
                textId="justWalletTitle"
                options={walletAccountsOptions}
              />
            )}
          </>
        )}
        <RadioButton
          name="refundMethod"
          textId="justToMyTradingAccount"
          value="tradingAccount"
          onChange={() => setRefundMethod('tradingAccount')}
          checked={refundMethod === 'tradingAccount'}
        />
        {refundMethod === 'tradingAccount' && (
          <Field component={ReduxSelectField} name="withdrawAccount" textId="justAccount" options={accountsOptions} />
        )}
      </div>

      <div className="RestartChallengeForm__bottom">
        <div className="RestartChallengeForm__RefundableFee">
          <FM id="justRefundableFee">{txt => <span>{txt}:</span>}</FM>
          <div>{challengeCost ?? '---'}</div>
        </div>

        <div className="DivWithDefButton">
          <DefaultButton
            className="challenge-button"
            textId={restartButtonTextId}
            type="submit"
            disabled={isSubmitButtonDisabled}
            loading={submitting}
            filled
          />
        </div>
      </div>

      {error && <strong className="formError">{error}</strong>}

      <style jsx>{staticStyles}</style>
    </form>
  );
};

const selector = formValueSelector('restartChallengeForm');
export default compose(
  FormMessageHOC,
  injectIntl,
  connect(
    state => {
      const { account, walletPaymentAccount, walletRefundAccount, withdrawAccount } = selector(
        state,
        'walletPaymentAccount',
        'walletRefundAccount',
        'account',
        'withdrawAccount'
      );
      return {
        account,
        walletPaymentAccount,
        walletRefundAccount,
        walletAccounts: state.client.multiWallet.walletAccounts.filter(
          account => !account.isArchived && !account.isDemo && !account.isBlocked && account.balance
        ),
        withdrawAccount,
        accounts: state.client.common.accounts,
        accountsIsLoaded: state.client.common.accountsIsLoaded,
        baseValue: state.interfaceConfig.baseValue,
        isWalletAccountAddonEnabled: state.interfaceConfig.addons?.some(addon => addon === 'wallet'),
        isMultiWalletAddonEnabled: state.interfaceConfig.addons?.some(addon => addon === 'walletAccounts'),
      };
    },
    {
      getTradingAccountList: () => getTradingAccountList.request(),
      getWalletAccounts: () => getWalletAccountsList.request(),
    }
  ),
  reduxForm({
    form: 'restartChallengeForm',
    onSubmitSuccess: (result, dispatch, props) => {
      props.toggleModal();
    },
    enableReinitialize: true,
  })
)(RestartChallengeForm);
